import axios, { AxiosInstance } from 'axios';
import type {
  AssetMetadata,
  BlockedNasPath,
  FilterOptions,
  PaginatedResponse,
  UpdateAssetRequest,
  ApiResponse,
  Activity,
  FolderInfo,
  ExplorerDirNode,
} from '@linein/shared';

interface TagDefinition {
  tagKey: string;
  category: string;
  name: string;
  usageCount?: number;
  createdAt?: string;
}

class ApiService {
  private client: AxiosInstance;
  private promoteInFlight = new Map<string, Promise<{ thumbnailUrl: string; hdUrl: string; originalUrl: string }>>();
  private promoteCache = new Map<string, { thumbnailUrl: string; hdUrl: string; originalUrl: string }>();
  private promoteLastAttemptAt = new Map<string, number>();

  constructor() {
    const configuredApiUrl = (import.meta.env.VITE_API_URL || '').trim();
    const normalizedBaseUrl =
      !configuredApiUrl || configuredApiUrl === '/' ? '/api' : configuredApiUrl;

    this.client = axios.create({
      baseURL: normalizedBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add auth token if available
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
      }
    );
  }

  // Assets
  async getAssets(
    filters?: FilterOptions,
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<AssetMetadata>> {
    const response = await this.client.get<ApiResponse<PaginatedResponse<AssetMetadata>>>(
      '/assets',
      {
        params: { ...filters, page, pageSize },
      }
    );
    return response.data.data!;
  }

  async getAssetById(id: string): Promise<AssetMetadata> {
    const response = await this.client.get<ApiResponse<AssetMetadata>>(`/assets/${id}`);
    return response.data.data!;
  }

  async uploadAsset(formData: FormData): Promise<{
    data: AssetMetadata;
    visionAnalysis?: {
      aiTags: string[];
      labelsCount: number;
      objectsCount: number;
      hasText: boolean;
    };
  }> {
    const response = await this.client.post<ApiResponse<AssetMetadata> & {
      visionAnalysis?: {
        aiTags: string[];
        labelsCount: number;
        objectsCount: number;
        hasText: boolean;
      };
    }>(
      '/assets/upload',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return {
      data: response.data.data!,
      visionAnalysis: response.data.visionAnalysis,
    };
  }

  async updateAsset(id: string, updates: UpdateAssetRequest): Promise<AssetMetadata> {
    const response = await this.client.patch<ApiResponse<AssetMetadata>>(
      `/assets/${id}`,
      updates
    );
    return response.data.data!;
  }

  async deleteAsset(id: string): Promise<void> {
    await this.client.delete(`/assets/${id}`);
  }

  async deleteAssets(ids: string[]): Promise<void> {
    await this.client.post('/assets/batch-delete', { ids });
  }

  async moveAssets(ids: string[], folderPath: string): Promise<void> {
    await this.client.post('/assets/batch-move', { ids, folderPath });
  }

  // Activities
  async getActivities(): Promise<Activity[]> {
    const response = await this.client.get<ApiResponse<Activity[]>>('/activities');
    return response.data.data!;
  }

  async getActivityById(id: string): Promise<Activity> {
    const response = await this.client.get<ApiResponse<Activity>>(`/activities/${id}`);
    return response.data.data!;
  }

  async createActivity(activity: Omit<Activity, 'id' | 'createdDate'>): Promise<Activity> {
    const response = await this.client.post<ApiResponse<Activity>>('/activities', activity);
    return response.data.data!;
  }

  // Folders
  async getFolderTree(): Promise<FolderInfo[]> {
    const response = await this.client.get<ApiResponse<FolderInfo[]>>('/folders');
    return response.data.data!;
  }

  async createFolder(path: string): Promise<FolderInfo> {
    const response = await this.client.post<ApiResponse<FolderInfo>>('/folders', { path });
    return response.data.data!;
  }

  async deleteFolder(path: string): Promise<void> {
    await this.client.delete(`/folders`, { data: { path } });
  }

  // Tags
  async getAllTags(): Promise<string[]> {
    const response = await this.client.get<ApiResponse<string[]>>('/tags');
    return response.data.data!;
  }

  async getTagDefinitions(): Promise<TagDefinition[]> {
    const response = await this.client.get<ApiResponse<TagDefinition[]>>('/tags/definitions');
    return response.data.data || [];
  }

  async createTagDefinition(input: {
    category: string;
    name: string;
  }): Promise<TagDefinition> {
    const response = await this.client.post<ApiResponse<TagDefinition>>('/tags/definitions', input);
    return response.data.data!;
  }

  async applyTagsToAssets(payload: {
    action: 'add' | 'remove';
    assetIds: string[];
    tagKeys: string[];
  }): Promise<{ updatedCount: number; updated: Array<{ id: string; tags: string[]; tagKeys: string[] }> }> {
    const response = await this.client.post<ApiResponse<{ updatedCount: number; updated: Array<{ id: string; tags: string[]; tagKeys: string[] }> }>>(
      '/tags/apply',
      payload
    );
    return response.data.data!;
  }

  // Search
  async searchAssetsByKeyword(
    keyword: string,
    page = 1,
    pageSize = 50
  ): Promise<{
    results: Array<{
      id: string;
      name: string;
      sourcePath: string;
      description?: string;
      tags: string[];
      aiTags: string[];
      aiDescription?: string;
      thumbnails: {
        thumbnail: string;
        hd: string;
        original: string;
      };
      sourceType: string;
      activityId?: string;
      uploadDate: string;
      fileSize?: number;
      mimeType?: string;
      resolution?: string;
      isStarred?: boolean;
    }>;
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    keyword: string;
  }> {
    const response = await this.client.get<ApiResponse<{
      results: Array<{
        id: string;
        name: string;
        sourcePath: string;
        description?: string;
        tags: string[];
        aiTags: string[];
        aiDescription?: string;
        thumbnails: {
          thumbnail: string;
          hd: string;
          original: string;
        };
        sourceType: string;
        activityId?: string;
        uploadDate: string;
        fileSize?: number;
        mimeType?: string;
        resolution?: string;
        isStarred?: boolean;
      }>;
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
      keyword: string;
    }>>('/assets/search/keyword', {
      params: { keyword, page, pageSize },
    });
    return response.data.data!;
  }

  // Storage
  async getSignedUrl(path: string): Promise<string> {
    const response = await this.client.get<ApiResponse<{ url: string }>>(
      '/storage/signed-url',
      {
        params: { path },
      }
    );
    return response.data.data!.url;
  }

  async getStorageStatus(): Promise<{
    usedGB: number;
    totalGB: number;
    usagePercentage: number;
    assetCount: number;
    lastUpdated: string;
  }> {
    const response = await this.client.get<
      ApiResponse<{
        usedGB: number;
        totalGB: number;
        usagePercentage: number;
        assetCount: number;
        lastUpdated: string;
      }>
    >('/storage/status');
    return response.data.data!;
  }

  async refreshStorageStatus(): Promise<void> {
    await this.client.post('/storage/refresh');
  }

  // ─── NAS Sync ────────────────────────────────────────────────────────────

  async triggerNasSync(rootPaths?: string[]): Promise<{
    id: string;
    triggeredBy: string;
    rootPaths: string[];
    startedAt: string;
    status: string;
    stats: Record<string, number>;
  }> {
    const response = await this.client.post<{ success: boolean; data: any }>(
      '/nas/sync',
      rootPaths ? { rootPaths } : {}
    );
    return response.data.data;
  }

  async getNasSyncCurrent(): Promise<{
    id: string;
    triggeredBy: string;
    rootPaths: string[];
    startedAt: string;
    status: 'running' | 'completed' | 'failed';
    stats: { scanned: number; added: number; updated: number; deleted: number; skipped: number; errors: number };
    currentFile?: string;
    errorMessage?: string;
  } | null> {
    const response = await this.client.get<{ success: boolean; data: any }>('/nas/sync/current');
    return response.data.data;
  }

  async getNasSyncHistory(limit = 20): Promise<Array<{
    id: string;
    triggeredBy: string;
    rootPaths: string[];
    startedAt: string;
    completedAt?: string;
    status: string;
    stats: { scanned: number; added: number; updated: number; deleted: number; skipped: number; errors: number };
    errorMessage?: string;
  }>> {
    const response = await this.client.get<{ success: boolean; data: any[] }>(
      '/nas/sync/history',
      { params: { limit } }
    );
    return response.data.data;
  }

  async pingNas(): Promise<{ reachable: boolean; nasHost: string; configuredPaths: string[] }> {
    const response = await this.client.get<{ success: boolean; data: any }>('/nas/ping');
    return response.data.data;
  }

  async promoteAssetToGcpFull(id: string): Promise<{
    thumbnailUrl: string;
    hdUrl: string;
    originalUrl: string;
  }> {
    const now = Date.now();
    const lastAttempt = this.promoteLastAttemptAt.get(id) || 0;
    if (now - lastAttempt < 3000) {
      const inFlightGuard = this.promoteInFlight.get(id);
      if (inFlightGuard) return inFlightGuard;
      const cachedGuard = this.promoteCache.get(id);
      if (cachedGuard) return cachedGuard;
    }

    const cached = this.promoteCache.get(id);
    if (cached) return cached;

    const inFlight = this.promoteInFlight.get(id);
    if (inFlight) return inFlight;

    this.promoteLastAttemptAt.set(id, now);

    const request = this.client
      .post<{ success: boolean; data: any }>(`/nas/assets/${id}/promote`)
      .then((response) => {
        const data = response.data.data as {
          thumbnailUrl: string;
          hdUrl: string;
          originalUrl: string;
        };
        this.promoteCache.set(id, data);
        return data;
      })
      .finally(() => {
        this.promoteInFlight.delete(id);
      });

    this.promoteInFlight.set(id, request);
    return request;
  }

  async getBlockedNasPaths(): Promise<BlockedNasPath[]> {
    const response = await this.client.get<ApiResponse<BlockedNasPath[]>>('/nas/blocked-paths');
    return response.data.data || [];
  }

  async blockNasPath(path: string): Promise<void> {
    await this.client.post('/nas/blocked-paths', { path });
  }

  async unblockNasPath(path: string): Promise<void> {
    await this.client.delete('/nas/blocked-paths', { data: { path } });
  }

  // ─── NAS Explorer ────────────────────────────────────────────────────────

  async getNasExplorerTree(parentPath: string | null): Promise<ExplorerDirNode[]> {
    const params: Record<string, string> = {};
    if (parentPath !== null) params.parentPath = parentPath;
    const response = await this.client.get<{ success: boolean; data: ExplorerDirNode[] }>(
      '/nas/explorer/tree',
      { params }
    );
    return response.data.data ?? [];
  }

  async getNasTreeFull(): Promise<ExplorerDirNode[]> {
    const response = await this.client.get<{ success: boolean; data: ExplorerDirNode[] }>(
      '/nas/explorer/tree-full'
    );
    return response.data.data ?? [];
  }

  async searchNasDirectoryTree(q: string): Promise<ExplorerDirNode[]> {
    const response = await this.client.get<{ success: boolean; data: ExplorerDirNode[] }>(
      '/nas/explorer/search',
      { params: { q } }
    );
    return response.data.data ?? [];
  }

  async rebuildNasDirectoryTree(): Promise<{ directoryCount: number; scanStarted: boolean; scanRunning: boolean }> {
    const response = await this.client.post<{ success: boolean; data: { directoryCount: number; scanStarted: boolean; scanRunning: boolean } }>(
      '/nas/explorer/rebuild'
    );
    return response.data.data;
  }

  async getNasDirScanStatus(): Promise<{
    running: boolean;
    startedAt: string | null;
    directoriesFound: number;
    completedAt: string | null;
    error: string | null;
  }> {
    const response = await this.client.get<{
      success: boolean;
      data: {
        running: boolean;
        startedAt: string | null;
        directoriesFound: number;
        completedAt: string | null;
        error: string | null;
      };
    }>('/nas/explorer/scan-status');
    return response.data.data;
  }

  // AI Analysis
  async analyzeImage(imageUrl?: string, imageBase64?: string): Promise<{
    tags: string[];
    description: string;
    activityNature: string[];
    assetNature: string[];
    visionAnalysis: any;
  }> {
    const response = await this.client.post<ApiResponse<{
      tags: string[];
      description: string;
      activityNature: string[];
      assetNature: string[];
      visionAnalysis: any;
    }>>('/ai/analyze', {
      imageUrl,
      imageBase64,
    });
    return response.data.data!;
  }
}

export const apiService = new ApiService();
export default apiService;
